import React, { useState } from 'react';
import { Scene } from '../types';
import MapPicker from './MapPicker';
import { useUploadCtx } from '../contexts/UploadContext';
import { CameraIcon, Building2, Cuboid as Cube, Car, MapPin, Layers, Lightbulb, X, Grid2X2, Grid3X3 } from 'lucide-react';

// Custom icon components based on the provided image
const DroneIcon: React.FC<{ className?: string; strokeWidth?: number }> = ({ className, strokeWidth = 1.5 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth={strokeWidth}
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M8 8H16M8 8C7 8 6 9 6 10V14C6 15 7 16 8 16M8 8V16M16 8C17 8 18 9 18 10V14C18 15 17 16 16 16M16 8V16M16 16H8" />
  </svg>
);

interface Props {
  onSubmit: (scene: Omit<Scene, 'id' | 'images' | 'quality' | 'thumbnail'>) => void;
  onCancel: () => void;
}

export default function NewSceneForm({ onSubmit, onCancel }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cameraType, setCameraType] = useState<Scene['cameraType'] | ''>('');
  const [capturePreset, setCapturePreset] = useState<Scene['capturePreset'] | ''>('');
  const [captureType, setCaptureType] = useState<number>(1); // Default to 1 camera
 
  const { mintNewSequenceUuid } = useUploadCtx();

  const cameraTypes = [
    { id: 'drone' as const, label: 'Drone',description: 'Aerial photography with drones' },
    { id: 'professional' as const, label: 'Professional',description: 'DSLR or mirrorless cameras' },
    { id: 'insta360' as const, label: 'Insta360',description: '360-degree panoramic capture (Unstitched images)' },
    { id: 'smartphone' as const, label: 'Smartphone',description: 'Mobile phone photography' }
  ];

  const capturePresets = [
    { id: 'drive' as const, label: 'Drive Pass', description: 'Use when recording from a moving vehicle', icon: Car },
    { id: 'indoor' as const, label: 'Indoor Walkthrough', description: 'Use for rooms, hallways, and interiors', icon: Building2 },
    { id: 'object' as const, label: 'Object Scan', description: 'Use when the subject is a specific object or a person', icon: Cube },
    { id: 'outdoor_walk' as const, label: 'Outdoor Walk', description: 'Use when walking an exterior environment', icon: MapPin },
    { id: 'drone' as const, label: 'Drone / Aerial', description: 'Use for top-down or oblique aerial flights', icon: DroneIcon },
    { id: 'mix_drone' as const, label: 'Mixed Ground + Aerial', description: 'Combine drone & ground data for full coverage', icon: Layers }
  ];

 /* const cameraCountOptions = [
    { id: 'single' as const, label: 'Single Camera', description: 'One camera', icon: CameraIcon },
    { id: 'dual' as const, label: 'Dual Cameras', description: 'Two camera', icon: Grid2X2 },
    { id: 'triple' as const, label: 'Triple Cameras', description: 'Three camera', icon: Grid3X3 },
    { id: 'quad' as const, label: 'Quad Cameras', description: 'Four camera°', icon: Grid3X3 }
  ];*/

  const [location, setLocation] = useState<{ latitude: number; longitude: number; name?: string } | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cameraType || !capturePreset || !captureType) return;
    
    // Generate a brand-new sequence UUID for this forthcoming upload/reconstruction cycle
    mintNewSequenceUuid();

    onSubmit({
      name: name.trim(),
      description: description.trim(),
      cameraType,
      capturePreset,
      captureType,
      ...(location ? { location } : {}),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      creatorId: '',
      status: 'pending',
      versions: []
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-6 z-50">
      <div className="bg-[#161513] rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#0CB4E4]/30">
        <div className="p-6 border-b border-[#0CB4E4]/30">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-white">Create New Scene</h2>
            <button
              onClick={onCancel}
              className="p-2 text-gray-300 hover:text-white hover:bg-[#161513] rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                Scene Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name for your scene" 
                className="w-full px-4 py-2.5 bg-[#161513] border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4]"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-white mb-2">
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description of your scene"
                rows={3}
                className="w-full px-4 py-2.5 bg-[#161513] border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] resize-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">Camera Type</label>
             

              <div className="relative">
                <select
                  value={cameraType}
                  onChange={(e) => setCameraType(e.target.value as Scene['cameraType'])}
                  className="w-full pl-4 pr-8 py-2.5 bg-[#161513] border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] appearance-none"
                  required
                >
                  <option value="">Select a camera type</option>
                  {cameraTypes.map(({ id, label, description }) => (
                    <option key={id} value={id}>
                      {label} - {description}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              
<div className="mt-2 flex items-start gap-3"> 
  
  
  <p className="text-sm text-gray-400">
    Tip:{' '}
    <a 
      href="https://apps.apple.com/us/app/cr3ate-ai/id6751804720" 
      target="_blank" 
      rel="noopener noreferrer" 
      className=" text-[#0CB4E4] hover:underline"
    >
      Download
    </a>
    {' '}our Cr3ate app. It's is the easiest way to get started.
  </p>
</div>

</div>

<div>
              <label className="block text-sm font-medium text-white mb-2">
                📷 Number of Cameras
              </label>
              <p className="text-xs text-gray-400 mb-3">
                Select how many synchronized cameras you used during capture
              </p>
              
              <div className="relative">
                <select
                  value={captureType}
                  onChange={(e) => setCaptureType(Number(e.target.value))}
                  className="w-full pl-4 pr-8 py-2.5 bg-[#161513] border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] appearance-none"
                  required
                >
                  {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num}>
                      {num} {num === 1 ? 'Camera' : 'Cameras'}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Capture Setup</label>
              {/* Hidden input to make group required for browser validation */}
              <input type="text" className="sr-only" tabIndex={-1} value={capturePreset} required onChange={()=>{}} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {capturePresets.map(({ id, label, description, icon }) => (
                  <label
                    key={id}
                    className={`relative flex items-start p-4 cursor-pointer rounded-xl border-2 transition-all ${
                      capturePreset === id
                        ? 'border-[#0CB4E4] bg-[#0CB4E4]/10'
                        : 'border-[#0CB4E4]/30 hover:border-[#0CB4E4]/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="capture-preset"
                      value={id}
                      checked={capturePreset === id}
                      onChange={(e) => setCapturePreset(e.target.value as Scene['capturePreset'])}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 text-[#0CB4E4]">
                          {React.createElement(icon as React.ElementType, { className: capturePreset === id ? 'text-[#0CB4E4]' : 'text-[#0CB4E4]', strokeWidth: 1.5 })}
                        </span>
                        <span className={`font-medium ${capturePreset === id ? 'text-[#0CB4E4]' : 'text-white'}`}>
                          {label}
                        </span>
                      </div>
                      <p className={`mt-1 text-sm ${capturePreset === id ? 'text-[#0CB4E4]/80' : 'text-gray-300'}`}>
                        {description}
                      </p>
                    </div>
                    <span
                      className={`absolute top-4 right-4 w-3 h-3 rounded-full border ${
                        capturePreset === id
                          ? 'border-[#0CB4E4] bg-[#0CB4E4]'
                          : 'border-[#0CB4E4]/50 bg-transparent'
                      }`}
                    />
                  </label>
                ))}
              </div>
            </div>

            {/* Map Picker */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Scene Location (optional)</label>
              <MapPicker value={location} onChange={setLocation} />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-300 hover:text-white font-medium hover:bg-[#161513] transition-colors rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || !cameraType || !capturePreset || !captureType}
              className="px-4 py-2 bg-[#0CB4E4] text-[#161513] font-medium hover:bg-[#0CB4E4]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md rounded-xl"
            >
              Create Scene
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
